import type { TFunction } from 'i18next';
import { useOutcomes } from '../../../api/strategy-client/strategy-client.hooks';
import type { Outcome } from '../../../api/strategy-client/strategy-client.type';
import type { StrategyLevelConfig } from '../strategy-config.type';
import { OutcomesFilters } from './outcomes-filters';
import { OutcomesMainSection } from './outcomes-main-section';
import { OutcomesOverview } from './outcomes-overview';
import { OutcomesSideSection } from './outcomes-side-section';

/**
 * Build the Outcomes strategy level configuration against the caller's
 * translation function so org-level text_config overrides apply.
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {StrategyLevelConfig<Outcome>} Strategy-level config bound to `t`
 */
export const getOutcomesConfig = (t: TFunction): StrategyLevelConfig<Outcome> => ({
  level: 'outcomes',
  tabId: 'outcomes',

  labels: {
    pageTitle: `${t('common.outcomes')} - Bloomfilter`,
    overviewTitle: 'KPIs Overview',
    filtersTitle: 'Filters',
    // Resolve through dedicated dictionary keys (with the lowercase formatter
    // applied at dictionary-resolution time) instead of String.toLowerCase()
    // here — runtime casing breaks locale-specific casing rules and prevents
    // translators from overriding the wording per language.
    emptyLabel: t('strategy.outcomes.empty'),
    byStatusTitle: t('strategy.outcomes.by_status'),
    byStateTitle: t('strategy.outcomes.by_state'),
    searchPlaceholder: t('strategy.outcomes.search_placeholder'),
  },

  features: {
    hasTreeMapView: false,
    hasCreatePresentation: true,
    hasExport: true,
    hasSearch: true,
    hasAddNew: true,
    hasFilters: true,
    hasSidePanel: true,
  },

  hooks: {
    useItems: () => {
      const { outcomes, query } = useOutcomes();
      return { items: outcomes, query };
    },
  },

  components: {
    Filters: OutcomesFilters,
    Overview: OutcomesOverview,
    MainSection: OutcomesMainSection,
    SideSection: OutcomesSideSection,
  },
});
